import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Truck, CheckCircle, Leaf, Star, Droplets, Thermometer, Shield, ArrowRight, Phone, Shovel } from 'lucide-react';
import SEO from '@/components/SEO';
import ExpandedFAQSection from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { soilFaqs } from '@/data/productFaqs';
import premiumSoilImage from '@/assets/premium-soil-mix.jpg';
import topsoil from '@/assets/topsoil.webp';
import mushroomCompost from '@/assets/mushroom-compost.webp';
import selectFill from '@/assets/select-fill.webp';

interface Product {
  id: string;
  slug_en: string;
  slug_es: string;
  name_en: string;
  name_es: string;
  description_en: string;
  description_es: string;
  features_en: string[];
  features_es: string[];
  is_featured: boolean;
  price_book: {
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }[];
}

const SoilPage = () => {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSoilProducts = async () => {
      try {
        const { data: category } = await supabase
          .from('categories')
          .select('id')
          .eq(`slug_${language}`, language === 'en' ? 'soil' : 'tierra')
          .single();

        if (category) {
          const { data: soilProducts } = await supabase
            .from('products')
            .select(`
              *,
              price_book (
                list_price,
                sale_price,
                unit_label_en,
                unit_label_es
              )
            `)
            .eq('category_id', category.id)
            .eq('is_active', true)
            .order('sort_order');

          setProducts(soilProducts || []);
        }
      } catch (error) {
        console.error('Error fetching soil products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSoilProducts();
  }, [language]);

  const getProductName = (product: Product) => 
    language === 'en' ? product.name_en : product.name_es;
  
  const getProductDescription = (product: Product) => 
    language === 'en' ? product.description_en : product.description_es;
  
  const getProductFeatures = (product: Product) => 
    language === 'en' ? product.features_en : product.features_es;

  const getUnitLabel = (product: Product) => 
    language === 'en' ? product.price_book[0]?.unit_label_en : product.price_book[0]?.unit_label_es;

  const seoTitle = language === 'en' 
    ? 'Premium Soil & Top Soil Delivery Texas | Garden & Compost'
    : 'Entrega de Tierra Premium Texas | Jardín y Compost';

  const seoDescription = language === 'en'
    ? 'Quality top soil, garden soil & compost delivery in Montgomery County TX. Rich, nutrient-dense soil perfect for planting. Professional delivery service.'
    : 'Entrega de tierra superior, tierra de jardín y compost en el Condado Montgomery TX. Tierra rica en nutrientes perfecta para plantar. Servicio de entrega profesional.';
  
  const seoKeywords = language === 'en'
    ? 'topsoil delivery texas, garden soil montgomery county, compost delivery conroe, fill dirt texas, soil delivery woodlands, bulk soil texas'
    : 'entrega tierra texas, tierra jardín montgomery county, entrega compost conroe, tierra relleno texas, entrega suelo woodlands, tierra granel texas';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": seoTitle,
    "description": seoDescription,
    "category": "Landscaping Supplies",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/soil' : '/es/tierra'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Products' : 'Productos', url: language === 'en' ? '/en/products' : '/es/productos' },
          { name: language === 'en' ? 'Soil & Topsoil' : 'Tierra', url: language === 'en' ? '/en/soil' : '/es/tierra' },
        ]}
      />
      <Header />
      <MobileCTA />
      
      <main>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-primary-dark py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-6">
            {language === 'en' ? 'Premium Soil & Top Soil' : 'Tierra Premium y Superior'}
          </h1>
          <p className="text-xl text-primary-foreground/90 mb-8 max-w-3xl mx-auto">
            {language === 'en' 
              ? 'Rich, nutrient-dense soil blends perfect for planting, gardening, and landscaping projects throughout Montgomery County, Texas.'
              : 'Mezclas de tierra rica en nutrientes perfectas para plantar, jardinería y proyectos de paisajismo en todo el Condado Montgomery, Texas.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="lg" asChild>
              <a href={`/${language}/quote`}>
                {language === 'en' ? 'Get a Quote' : 'Obtener Cotización'}
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild className="bg-white/95 border-white text-primary hover:bg-white hover:text-primary">
              <a href="tel:9363200763">
                {language === 'en' ? 'Call (936) 320-0763' : 'Llamar (936) 320-0763'}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Our Soil Products' : 'Nuestros Productos de Tierra'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Choose from our selection of premium soil blends, each specially formulated for optimal plant growth and garden success.'
                : 'Elija de nuestra selección de mezclas de tierra premium, cada una especialmente formulada para un crecimiento óptimo de plantas y éxito en el jardín.'
              }
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-20 bg-muted rounded mb-4"></div>
                    <div className="h-8 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-xl group-hover:text-primary transition-colors">
                        {getProductName(product)}
                        {product.is_featured && (
                          <Star className="inline-block ml-2 h-5 w-5 text-accent fill-current" />
                        )}
                      </CardTitle>
                      {product.price_book[0]?.sale_price && (
                        <Badge variant="destructive">
                          {language === 'en' ? 'Sale' : 'Oferta'}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      {getProductDescription(product)}
                    </p>
                    
                    {/* Features */}
                    <div className="space-y-2">
                      {getProductFeatures(product).slice(0, 4).map((feature, index) => (
                        <div key={index} className="flex items-center text-sm">
                          <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Pricing */}
                    <div className="pt-4 border-t">
                      <div className="flex items-baseline justify-between">
                        <div>
                          {product.price_book[0]?.sale_price ? (
                            <>
                              <span className="text-lg font-bold text-destructive">
                                ${product.price_book[0].sale_price}
                              </span>
                              <span className="text-sm text-muted-foreground line-through ml-2">
                                ${product.price_book[0].list_price}
                              </span>
                            </>
                          ) : (
                            <span className="text-lg font-bold text-primary">
                              ${product.price_book[0]?.list_price}
                            </span>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {getUnitLabel(product)}
                          </div>
                        </div>
                        <Button size="sm" className="group-hover:bg-primary-dark transition-colors">
                          {language === 'en' ? 'Add to Quote' : 'Agregar a Cotización'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-muted py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Why Choose Our Soil?' : '¿Por Qué Elegir Nuestra Tierra?'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Leaf className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {language === 'en' ? 'Nutrient Rich' : 'Rico en Nutrientes'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Our soil blends are packed with essential nutrients and organic matter for healthy plant growth.'
                  : 'Nuestras mezclas de tierra están llenas de nutrientes esenciales y materia orgánica para un crecimiento saludable de plantas.'
                }
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {language === 'en' ? 'Tested Quality' : 'Calidad Probada'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Every batch is tested to ensure optimal pH levels and nutrient content for your projects.'
                  : 'Cada lote es probado para asegurar niveles óptimos de pH y contenido de nutrientes para sus proyectos.'
                }
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {language === 'en' ? 'Fast Delivery' : 'Entrega Rápida'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Professional delivery service throughout Montgomery County with flexible scheduling.'
                  : 'Servicio de entrega profesional en todo el Condado Montgomery con horarios flexibles.'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Tips */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Soil Installation Tips' : 'Consejos de Instalación de Tierra'}
            </h2>
          </div>
          
          <div className="bg-primary/5 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-primary">
                  {language === 'en' ? 'Preparation' : 'Preparación'}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Remove existing grass and weeds from the area'
                        : 'Remover pasto y malezas existentes del área'
                      }
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Test existing soil pH and drainage'
                        : 'Probar pH y drenaje de la tierra existente'
                      }
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Level the ground and add amendments if needed'
                        : 'Nivelar el suelo y agregar enmiendas si es necesario'
                      }
                    </span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4 text-primary">
                  {language === 'en' ? 'Application' : 'Aplicación'}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Spread soil evenly to desired depth (3-6 inches)'
                        : 'Extender tierra uniformemente a la profundidad deseada (3-6 pulgadas)'
                      }
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Rake smooth and remove any debris'
                        : 'Rastrillar suavemente y remover cualquier escombro'
                      }
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {language === 'en' 
                        ? 'Water lightly to settle and begin planting'
                        : 'Regar ligeramente para asentar y comenzar a plantar'
                      }
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ExpandedFAQSection faqs={soilFaqs} language={language as 'en' | 'es'} />
      <RelatedMaterialsLinks language={language as 'en' | 'es'} exclude={['soil']} />
      </main>

      <Footer />
    </div>
  );
};

export default SoilPage;